// src/features/project/BoardPanel.tsx
// Kanban board: columns from GetBoardView, item cards, board switcher (ListBoards),
// and "add item". Card click opens the right rail (where MoveItem lives — no DnD lib).

import { useEffect, useState, useCallback } from 'react';
import { observer } from '@react';
import { useI18n, useProjectBoard } from '@react/hooks';
import { projectApi } from '@core/api';
import { Button, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { AddItemDialog } from './AddItemDialog';
import { idOf, listParams } from './meta';
import type { Entity } from '@core/types/entity';
import type { Board, BoardColumn, BoardColumnView, ProjectItem } from '@core/types/project';

interface BoardPanelProps {
  projectEntity: Entity;
  selectedItemId: string | null;
  onSelectItem: (item: ProjectItem, columns: BoardColumn[]) => void;
  onItemsChanged: () => void;
  refreshToken: number;
}

export const BoardPanel = observer(
  ({ projectEntity, selectedItemId, onSelectItem, onItemsChanged, refreshToken }: BoardPanelProps) => {
    const { t } = useI18n();
    const boardStore = useProjectBoard();
    const projectId = projectEntity.id;

    const [boards, setBoards] = useState<Board[]>([]);
    const [boardId, setBoardId] = useState('');
    const [columns, setColumns] = useState<BoardColumnView[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);
    const [showAdd, setShowAdd] = useState(false);

    // Load the board list once.
    useEffect(() => {
      let alive = true;
      boardStore.ListBoards(projectId, listParams()).then(() => {
        if (!alive) return;
        const list = [...boardStore.items];
        setBoards(list);
        const def = list.find((b) => b.is_default) ?? list[0];
        setBoardId(def ? idOf(def) : '');
        if (!def) setLoading(false);
      });
      return () => {
        alive = false;
      };
    }, [projectId]);

    const loadView = useCallback(
      async (id: string) => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
          const res = await projectApi.GetBoardView(projectId, id);
          setColumns(res.columns ?? []);
        } catch (e) {
          setError(e);
        } finally {
          setLoading(false);
        }
      },
      [projectId],
    );

    useEffect(() => {
      if (boardId) loadView(boardId);
    }, [boardId, refreshToken, loadView]);

    const boardColumns = columns.map((c) => c.column!).filter(Boolean);

    if (loading && columns.length === 0) {
      return <BoardSkeleton />;
    }

    if (error) {
      return <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />;
    }

    if (boards.length === 0) {
      const createDefaultBoard = async () => {
        setLoading(true);
        try {
          await boardStore.CreateBoard(projectId, {
            project: projectEntity,
            name: t('project.board.defaultName'),
            columns: {
              columns: [
                { id: '', name: t('project.board.colTodo'), order: 0, status_mappings: [], category: 'COLUMN_CATEGORY_TODO', is_collapsed: false },
                { id: '', name: t('project.board.colInProgress'), order: 1, status_mappings: [], category: 'COLUMN_CATEGORY_IN_PROGRESS', is_collapsed: false },
                { id: '', name: t('project.board.colDone'), order: 2, status_mappings: [], category: 'COLUMN_CATEGORY_DONE', is_collapsed: false },
              ],
            },
          } as never);
          await boardStore.ListBoards(projectId, listParams());
          const list = [...boardStore.items];
          setBoards(list);
          const def = list.find((b) => b.is_default) ?? list[0];
          setBoardId(def ? idOf(def) : '');
        } catch (e) {
          setError(e);
        } finally {
          setLoading(false);
        }
      };
      return (
        <div className="py-16 text-center">
          <p className="text-lg font-medium text-foreground">{t('project.board.none')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('project.board.noneDesc')}</p>
          <Button size="sm" className="mt-4" onClick={createDefaultBoard}>
            {t('project.board.createDefault')}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="w-56">
            <Select
              value={boardId}
              onChange={(e) => setBoardId(e.target.value)}
              aria-label={t('project.board.switch')}
            >
              {boards.map((b) => (
                <option key={idOf(b)} value={idOf(b)}>
                  {b.name}
                </option>
              ))}
            </Select>
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            {t('project.item.add')}
          </Button>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map((col) => (
            <div key={col.column?.id} className="w-72 flex-shrink-0">
              <div className="mb-2 flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  {col.column?.color && (
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: col.column.color }}
                      aria-hidden="true"
                    />
                  )}
                  <span className="text-sm font-medium text-foreground">{col.column?.name}</span>
                  <Badge variant="secondary" size="sm">
                    {col.item_count ?? col.items?.items?.length ?? 0}
                  </Badge>
                </div>
                {col.column?.wip_limit ? (
                  <span className="text-xs text-muted-foreground">
                    {t('project.board.wip')} {col.column.wip_limit}
                  </span>
                ) : null}
              </div>
              <div className="min-h-[120px] space-y-2 rounded-lg bg-muted/30 p-2">
                {(col.items?.items ?? []).map((item) => (
                  <ItemCard
                    key={idOf(item)}
                    item={item}
                    selected={idOf(item) === selectedItemId}
                    onClick={() => onSelectItem(item, boardColumns)}
                  />
                ))}
                {(col.items?.items?.length ?? 0) === 0 && (
                  <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                    {t('project.board.emptyColumn')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <AddItemDialog
          open={showAdd}
          projectEntity={projectEntity}
          onClose={() => setShowAdd(false)}
          onAdded={onItemsChanged}
        />
      </div>
    );
  },
);

BoardPanel.displayName = 'BoardPanel';

function ItemCard({
  item,
  selected,
  onClick,
}: {
  item: ProjectItem;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        selected ? 'border-primary ring-1 ring-primary/30' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="line-clamp-2 text-sm text-foreground">{item.title || item.item_key}</span>
        {item.story_points != null && (
          <span className="flex-shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {item.story_points}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        {item.item_key && <span className="font-mono text-[11px] text-muted-foreground">{item.item_key}</span>}
        {(item.labels ?? []).slice(0, 3).map((l) => (
          <span
            key={l.name}
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: l.color || undefined }}
            aria-hidden="true"
          />
        ))}
      </div>
    </button>
  );
}

function BoardSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="w-72 flex-shrink-0">
          <div className="mb-3 h-4 w-24 animate-pulse rounded bg-muted" />
          <div className="min-h-[200px] space-y-3 rounded-lg bg-muted/30 p-2">
            {Array.from({ length: 3 }).map((_, j) => (
              <div key={j} className="h-16 animate-pulse rounded-lg border border-border bg-card" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
